import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { X, Pencil } from "lucide-react";
import { supabase } from "../lib/supabase";

// Updates a single token's service_name + service_id directly via Supabase
async function updateTokenServices(tokenId, { service_name, service_id }) {
  const { error } = await supabase
    .from("tokens")
    .update({ service_name, service_id })
    .eq("id", tokenId);
  if (error) throw error;
}

export default function EditTokenServicesModal({ token, services, onClose }) {
  const initialIds = new Set(
    services
      .filter((s) => (token.service_name || "").split(" + ").map((x) => x.trim()).includes(s.name))
      .map((s) => s.id)
  );
  if (initialIds.size === 0 && token.service_id) initialIds.add(token.service_id);

  const [selected, setSelected] = useState(initialIds);
  const [saving, setSaving] = useState(false);

  const sel = services.filter((s) => selected.has(s.id));
  const totalMin = sel.reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
  const totalPrice = sel.reduce((sum, s) => sum + Number(s.price || 0), 0);

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function save() {
    if (selected.size === 0) {
      toast.error("Select at least one service");
      return;
    }
    setSaving(true);
    try {
      await updateTokenServices(token.id, {
        service_name: sel.map((s) => s.name).join(" + "),
        service_id: sel[0]?.id || null,
      });
      toast.success(`Token #${token.token_number} updated`);
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-5"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.96 }}
        transition={{ duration: 0.35 }}
        onClick={(e) => e.stopPropagation()}
        className="glass-strong rounded-2xl w-full max-w-md p-6 md:p-8"
        data-testid="edit-token-modal"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-gold flex items-center gap-2">
              <Pencil className="w-3.5 h-3.5" /> Update Services
            </div>
            <div className="font-heading text-2xl mt-1">
              Token #{token.token_number} · {token.customer_name}
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-6">
          <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
            Services (tap to add/remove)
          </label>
          <div className="mt-2 max-h-72 overflow-y-auto pr-1 space-y-2">
            {services.map((s) => {
              const isOn = selected.has(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggle(s.id)}
                  data-testid={`edit-svc-${s.id}`}
                  className={`w-full text-left rounded-lg px-3 py-2.5 border text-sm transition-all flex items-center justify-between ${
                    isOn
                      ? "border-gold bg-gold/5 text-gold"
                      : "border-white/10 text-white/70 hover:border-white/30"
                  }`}
                >
                  <span>{s.name}</span>
                  <span className="font-numbers text-[10px] tracking-wider">
                    {s.duration_minutes}m · ₹{Number(s.price).toLocaleString()}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-3 rounded-lg bg-gold/5 border border-gold/20 px-3 py-2 flex items-center justify-between">
            <div className="text-[10px] font-numbers tracking-[0.25em] uppercase text-white/60">
              {selected.size} selected · {totalMin}m
            </div>
            <div className="font-numbers text-sm text-gold tabular-nums">
              ₹{totalPrice.toLocaleString()}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="edit-token-save"
            className="btn-gold text-sm py-2.5 px-5"
          >
            {saving ? "Saving…" : "Update Services"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
